import { BLACK_TEXT, BLUE_600, GRAY_1000, GRAY_300, GRAY_700, RhetorikFontFamily } from 'constants/styling/theme'
import { Box, Stack, SxProps, Typography } from '@mui/material'
import { FC, useEffect, useMemo } from 'react'
import { PLAN_COPY_KEY, PLAN_FEATURES, PLAN_ICONS, PLAN_RANK, PRO_PERIOD_KEY, PRO_PRICE_UNAVAILABLE_KEY, PlanCardVariant, getEverythingPrefixKey } from 'constants/subscription'
import { SubscriptionPeriod, SubscriptionPlanType } from 'models/user/userWorkspaceDTOs'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { PlanFeatureList } from './PlanFeatureList.component'
import { PlanIconCircle } from './PlanIconCircle.component'
import { logger } from 'utils/logging'
import { usePlanPricing } from 'utils/hooks'
import { useTranslation } from 'react-i18next'

const currentPlanBadgeSx: SxProps = {
  position: 'absolute',
  top: 0,
  left: '50%',
  transform: 'translate(-50%, -50%)',
}

const priceContainerSx: SxProps = {
  padding: '1.6rem 0',
  borderBottom: `1px solid ${GRAY_300}`,
  mb: '1.6rem',
  justifyContent: 'flex-end',
}

const PlanCardPrice: FC<{
  planType: SubscriptionPlanType
  proPrice: { amount: string; suffix: string; crossedAmount: string | null } | null
}> = ({ planType, proPrice }) => {
  const { t } = useTranslation(['pricing_page'])

  return (
    <Stack sx={priceContainerSx}>
      {planType === SubscriptionPlanType.PRO && !proPrice && (
        <Typography variant="text-sm" color={BLACK_TEXT} fontWeight={550}>
          {t(PRO_PRICE_UNAVAILABLE_KEY)}
        </Typography>
      )}

      {planType === SubscriptionPlanType.PRO && proPrice && (
        <Box>

          {proPrice.crossedAmount && (
            <Typography component="span" variant="text-md" fontWeight={400} color={GRAY_700} sx={{ textDecoration: 'line-through', mr: '0.6rem' }}>
              {proPrice.crossedAmount}
            </Typography>
          )}

          <Typography component="span" variant="text-md" fontWeight={proPrice.crossedAmount ? 700 : 400} color={GRAY_1000}>
            {proPrice.amount}
          </Typography>

          <Typography component="span" variant="text-sm" color={GRAY_700}>
            {proPrice.suffix}
          </Typography>

        </Box>
      )}

      {planType === SubscriptionPlanType.PERSONAL && (
        <Typography variant="text-sm" color={BLACK_TEXT} fontWeight={550}>
          {t('pricing_page:plans.starter.price_display')}
        </Typography>
      )}

      {planType === SubscriptionPlanType.ENTERPRISE && (
        <Typography variant="text-sm" color={BLACK_TEXT} fontWeight={550}>
          {t('pricing_page:plans.enterprise.price_label')}
        </Typography>
      )}
    </Stack>
  )
}

interface Props {
  /** The plan this card represents */
  planType: SubscriptionPlanType
  /** Selected billing period (monthly or yearly) */
  period: SubscriptionPeriod
  /** Callback when the card's action button is clicked */
  onSelect: () => void
  /** Display variant: subscribe (pricing page) or manage (modal) */
  variant?: PlanCardVariant
  /** The workspace's active plan, used to determine upgrade/downgrade and current plan state */
  currentPlan?: SubscriptionPlanType
  /** Whether the user is still eligible for a trial, shown on the PRO subscribe button */
  trialAllowed?: boolean
}

export const PlanCard: FC<Props> = ({ planType, period, onSelect, variant = PlanCardVariant.SUBSCRIBE, currentPlan, trialAllowed = false }) => {
  const isCurrentPlan = planType === currentPlan
  const { t } = useTranslation(['pricing_page'])

  const { currency, seatMonthlyPrice, formatAmount } = usePlanPricing()

  const Icon = PLAN_ICONS[planType]
  const features = PLAN_FEATURES[planType]

  const copyKey = PLAN_COPY_KEY[planType]
  const planName = t(`pricing_page:plans.${copyKey}.name`)
  const tagline = t(`pricing_page:plans.${copyKey}.tagline`)

  const proPrice = useMemo(() => {
    if (planType !== SubscriptionPlanType.PRO) return null
    const amount = seatMonthlyPrice(period)
    if (amount === null) return null
    // When yearly is selected, show the monthly price crossed out before the discounted amount
    const monthlyAmount = period === SubscriptionPeriod.YEARLY ? seatMonthlyPrice(SubscriptionPeriod.MONTHLY) : null
    return {
      amount: formatAmount(amount),
      suffix: `/${t(PRO_PERIOD_KEY)}`,
      crossedAmount: monthlyAmount === null ? null : formatAmount(monthlyAmount),
    }
  }, [planType, period, seatMonthlyPrice, formatAmount, t])

  // The card quietly drops to a label when the customer's currency carries no figure, which is
  // invisible in production unless it says so — this is the surface most customers see.
  const isPriceUnavailable = planType === SubscriptionPlanType.PRO && !proPrice
  useEffect(() => {
    if (isPriceUnavailable) {
      logger.warn('No plan price is published for the customer currency', undefined, {
        context: {
          component: 'PlanCard',
          action: 'seatMonthlyPrice',
          metadata: { currency, period },
        },
      })
    }
  }, [isPriceUnavailable, currency, period])

  const everythingPrefixKey = getEverythingPrefixKey(planType)

  const isDowngrade = variant === PlanCardVariant.MANAGE
    && currentPlan !== undefined
    && PLAN_RANK[planType] < PLAN_RANK[currentPlan]

  const buttonLabel = useMemo(() => {
    if (isCurrentPlan) return t('pricing_page:current_plan')
    if (planType === SubscriptionPlanType.ENTERPRISE) return t('pricing_page:plans.enterprise.cta')
    if (planType === SubscriptionPlanType.PRO && trialAllowed) return t('pricing_page:plans.pro.cta')
    return t('pricing_page:subscribe')
  }, [isCurrentPlan, planType, trialAllowed, t])

  return (
    <BorderBoxWrapper
      display="flex"
      flexDirection="column"
      padding={2.4}
      position="relative"
      elevation={isCurrentPlan ? 'xs' : 'sm'}
      border={isCurrentPlan ? '2px solid' : '1px solid'}
      borderColor={isCurrentPlan ? BLUE_600 : GRAY_300}
    >
      {isCurrentPlan && (
        <Box sx={currentPlanBadgeSx}>
          <MUIBadge label={t('pricing_page:current_plan')} color="blue" size="sm" />
        </Box>
      )}

      <Box mb="1.6rem">
        <PlanIconCircle size="sm">
          <Icon />
        </PlanIconCircle>
      </Box>

      <Typography variant="text-xl" fontWeight={400} color={GRAY_1000} sx={{ fontFamily: RhetorikFontFamily, mb: '0.4rem' }}>
        {planName}
      </Typography>
      <Typography variant="text-sm" fontWeight={350} color={GRAY_700} mb="1.6rem">
        {tagline}
      </Typography>

      <PlanCardPrice planType={planType} proPrice={proPrice} />

      {everythingPrefixKey && (
        <Typography variant="text-sm" fontWeight={450} color={BLACK_TEXT} sx={{ display: 'block', mb: '1.6rem' }}>
          {t(everythingPrefixKey)}
        </Typography>
      )}

      <Stack gap="1.2rem" flex={1} mb="2.4rem">
        <PlanFeatureList features={features} size="sm" />
      </Stack>

      {!isDowngrade && (
        <MUIButton
          type="defaultPrimary"
          fullWidth
          disabled={isCurrentPlan}
          onClick={onSelect}
        >
          {buttonLabel}
        </MUIButton>
      )}
    </BorderBoxWrapper>
  )
}
